import { useRef, useEffect, useCallback, useState } from 'react';
import './BlogEditor.css';

const UPLOAD_API = '/api/upload.php';

/* ── Toolbar definition ─────────────────────────────────────────────────────── */
const TOOLBAR = [
  {
    group: 'block',
    items: [
      { type: 'block', cmd: 'formatBlock', val: 'h1', label: 'H1', title: 'Heading 1' },
      { type: 'block', cmd: 'formatBlock', val: 'h2', label: 'H2', title: 'Heading 2' },
      { type: 'block', cmd: 'formatBlock', val: 'h3', label: 'H3', title: 'Heading 3' },
      { type: 'block', cmd: 'formatBlock', val: 'p',  label: '¶',  title: 'Paragraph' },
    ],
  },
  {
    group: 'inline',
    items: [
      { type: 'inline', cmd: 'bold',          val: null, label: 'B', title: 'Bold (Ctrl+B)',      cls: 'rte-btn--bold' },
      { type: 'inline', cmd: 'italic',        val: null, label: 'I', title: 'Italic (Ctrl+I)',    cls: 'rte-btn--italic' },
      { type: 'inline', cmd: 'underline',     val: null, label: 'U', title: 'Underline (Ctrl+U)', cls: 'rte-btn--underline' },
      { type: 'inline', cmd: 'strikeThrough', val: null, label: 'S', title: 'Strikethrough',      cls: 'rte-btn--strike' },
    ],
  },
  {
    group: 'align',
    items: [
      { type: 'inline', cmd: 'justifyLeft',   val: null, label: '⬤≡', title: 'Align Left' },
      { type: 'inline', cmd: 'justifyCenter', val: null, label: '≡≡', title: 'Align Center' },
      { type: 'inline', cmd: 'justifyRight',  val: null, label: '≡⬤', title: 'Align Right' },
    ],
  },
  {
    group: 'list',
    items: [
      { type: 'inline', cmd: 'insertUnorderedList', val: null,        label: '• —', title: 'Bullet List' },
      { type: 'inline', cmd: 'insertOrderedList',   val: null,        label: '1 —', title: 'Numbered List' },
      { type: 'block',  cmd: 'formatBlock', val: 'blockquote',        label: '❝',   title: 'Block Quote' },
      { type: 'block',  cmd: 'formatBlock', val: 'pre',               label: '</>', title: 'Code Block' },
    ],
  },
];

function countWords(html) {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.split(' ').length : 0;
}

function estimateReadTime(wc) {
  return `${Math.max(1, Math.round(wc / 200))} min read`;
}

/* ── Component ──────────────────────────────────────────────────────────────── */
export default function BlogEditor({ value = '', onChange, onReadTimeChange, token = '' }) {
  const editorRef   = useRef(null);
  const fileRef     = useRef(null);
  const isInit      = useRef(false);
  const savedRange  = useRef(null);   // selection saved before panel opens

  const [wordCount,     setWordCount]     = useState(0);
  const [activeFormats, setActiveFormats] = useState({});
  const [imgPanel,      setImgPanel]      = useState(false);
  const [imgTab,        setImgTab]        = useState('upload');  // 'upload' | 'url'
  const [imgUrl,        setImgUrl]        = useState('');
  const [imgAlt,        setImgAlt]        = useState('');
  const [uploading,     setUploading]     = useState(false);
  const [uploadErr,     setUploadErr]     = useState('');

  /* initialise content once */
  useEffect(() => {
    if (!isInit.current && editorRef.current) {
      editorRef.current.innerHTML = value || '';
      isInit.current = true;
      const wc = countWords(value);
      setWordCount(wc);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    onChange(html);
    const wc = countWords(html);
    setWordCount(wc);
    onReadTimeChange?.(estimateReadTime(wc));
    updateActiveFormats();
  }, [onChange, onReadTimeChange]);

  const updateActiveFormats = useCallback(() => {
    const formats = {};
    ['bold', 'italic', 'underline', 'strikeThrough',
     'justifyLeft', 'justifyCenter', 'justifyRight',
     'insertUnorderedList', 'insertOrderedList'].forEach(cmd => {
      try { formats[cmd] = document.queryCommandState(cmd); } catch {}
    });
    try { formats._block = document.queryCommandValue('formatBlock').toLowerCase(); } catch {}
    setActiveFormats(formats);
  }, []);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    sync();
  }, [sync]);

  const insertLink = useCallback(() => {
    const sel          = window.getSelection();
    const selectedText = sel?.toString() || '';
    const url = prompt('Enter link URL (e.g. https://example.com):', 'https://');
    if (!url || url === 'https://') return;
    const trimmedUrl = url.trim();
    const proto = trimmedUrl.toLowerCase();
    if (!proto.startsWith('https://') && !proto.startsWith('http://') && !proto.startsWith('/') && !proto.startsWith('#')) return;
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    editorRef.current?.focus();
    if (selectedText) {
      document.execCommand('createLink', false, esc(trimmedUrl));
    } else {
      const text = prompt('Enter link text:', trimmedUrl) || trimmedUrl;
      document.execCommand('insertHTML', false,
        `<a href="${esc(trimmedUrl)}" target="_blank" rel="noopener noreferrer">${esc(text)}</a>`);
    }
    sync();
  }, [sync]);

  /* Save cursor position before opening panel so we can restore it on insert */
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (!savedRange.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange.current);
  }, []);

  /* Insert <figure> with img into editor at saved cursor position */
  const insertImgHTML = useCallback((src, alt) => {
    const trimmedSrc = (src || '').trim();
    if (!trimmedSrc || (!/^https?:\/\//i.test(trimmedSrc) && !trimmedSrc.startsWith('/'))) return;
    const esc = (s) => (s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const safeSrc = esc(trimmedSrc);
    const safeAlt = esc(alt || '');
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand('insertHTML', false,
      `<figure class="rte-figure"><img src="${safeSrc}" alt="${safeAlt}" class="rte-img" />${safeAlt ? `<figcaption>${safeAlt}</figcaption>` : ''}</figure>`);
    sync();
    setImgPanel(false);
    setImgUrl('');
    setImgAlt('');
    setUploadErr('');
  }, [restoreSelection, sync]);

  /* Upload file → get server path → insert */
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';   // reset so same file can be re-selected

    setUploading(true);
    setUploadErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res  = await fetch(UPLOAD_API, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    form,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Upload failed.');
      insertImgHTML(data.path, imgAlt || file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setUploadErr(err.message);
    } finally {
      setUploading(false);
    }
  }, [token, imgAlt, insertImgHTML]);

  /* Insert from URL tab */
  const insertFromUrl = useCallback(() => {
    if (!imgUrl.trim()) return;
    insertImgHTML(imgUrl.trim(), imgAlt.trim());
  }, [imgUrl, imgAlt, insertImgHTML]);

  const openImgPanel = useCallback(() => {
    saveSelection();
    setImgTab('upload');
    setImgUrl('');
    setImgAlt('');
    setUploadErr('');
    setImgPanel(p => !p);
  }, [saveSelection]);

  const insertHR = useCallback(() => {
    editorRef.current?.focus();
    document.execCommand('insertHorizontalRule');
    sync();
  }, [sync]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
      sync();
    }
    if (e.key === 'Escape' && imgPanel) setImgPanel(false);
  }, [sync, imgPanel]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    sync();
  }, [sync]);

  const isActive = (item) =>
    item.type === 'block' ? activeFormats._block === item.val : !!activeFormats[item.cmd];

  return (
    <div className="rte-wrap">
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="rte-toolbar" role="toolbar" aria-label="Editor toolbar">
        {TOOLBAR.map((group) => (
          <div key={group.group} className="rte-group">
            {group.items.map((item) => (
              <button
                key={item.label + item.cmd}
                type="button"
                title={item.title}
                className={`rte-btn${item.cls ? ' ' + item.cls : ''}${isActive(item) ? ' rte-btn--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); exec(item.cmd, item.val); }}
              >
                {item.label}
              </button>
            ))}
          </div>
        ))}

        {/* Special actions */}
        <div className="rte-group">
          <button type="button" title="Insert Link" className="rte-btn"
            onMouseDown={(e) => { e.preventDefault(); insertLink(); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
          <button
            type="button"
            title="Insert Image"
            className={`rte-btn${imgPanel ? ' rte-btn--active' : ''}`}
            onMouseDown={(e) => { e.preventDefault(); openImgPanel(); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </button>
          <button type="button" title="Horizontal Divider" className="rte-btn rte-btn--hr"
            onMouseDown={(e) => { e.preventDefault(); insertHR(); }}>
            —
          </button>
        </div>

        <div className="rte-group">
          <button type="button" title="Undo (Ctrl+Z)" className="rte-btn"
            onMouseDown={(e) => { e.preventDefault(); exec('undo'); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
            </svg>
          </button>
          <button type="button" title="Redo (Ctrl+Y)" className="rte-btn"
            onMouseDown={(e) => { e.preventDefault(); exec('redo'); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 14 20 9 15 4"/><path d="M4 20v-7a4 4 0 0 1 4-4h12"/>
            </svg>
          </button>
          <button type="button" title="Remove Formatting" className="rte-btn rte-btn--clear"
            onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Image insert panel ───────────────────────────────────────────────── */}
      {imgPanel && (
        <div className="rte-img-panel">
          <div className="rte-img-panel-tabs">
            <button
              type="button"
              className={`rte-img-tab${imgTab === 'upload' ? ' rte-img-tab--active' : ''}`}
              onClick={() => setImgTab('upload')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
              Upload File
            </button>
            <button
              type="button"
              className={`rte-img-tab${imgTab === 'url' ? ' rte-img-tab--active' : ''}`}
              onClick={() => setImgTab('url')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              Paste URL
            </button>
            <button type="button" className="rte-img-panel-close" onClick={() => setImgPanel(false)}>
              ✕
            </button>
          </div>

          <div className="rte-img-panel-body">
            {/* Alt text shared by both tabs */}
            <input
              className="rte-img-input"
              type="text"
              placeholder="Alt text (accessibility description)"
              value={imgAlt}
              onChange={e => setImgAlt(e.target.value)}
            />

            {imgTab === 'upload' ? (
              <div className="rte-img-upload-area">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  className="rte-img-upload-btn"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? (
                    <>
                      <span className="rte-img-spinner" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                      </svg>
                      Choose Image (JPEG · PNG · GIF · WebP, max 10 MB)
                    </>
                  )}
                </button>
                {uploadErr && <p className="rte-img-err">{uploadErr}</p>}
              </div>
            ) : (
              <div className="rte-img-url-row">
                <input
                  className="rte-img-input rte-img-url-input"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imgUrl}
                  onChange={e => setImgUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') insertFromUrl(); }}
                />
                <button
                  type="button"
                  className="rte-img-insert-btn"
                  disabled={!imgUrl.trim()}
                  onClick={insertFromUrl}
                >
                  Insert
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Content area ────────────────────────────────────────────────────── */}
      <div
        ref={editorRef}
        className="rte-content"
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        onKeyDown={handleKeyDown}
        onKeyUp={updateActiveFormats}
        onMouseUp={updateActiveFormats}
        onPaste={handlePaste}
        data-placeholder="Start writing your blog post here…"
      />

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <div className="rte-footer">
        <span className="rte-wordcount">{wordCount} words · {estimateReadTime(wordCount)}</span>
        <span className="rte-hint">Tip: Select text then click a format button · Tab to indent · Ctrl+Z to undo</span>
      </div>
    </div>
  );
}
